import React, { createContext, useContext, useState, useCallback, ReactNode, useEffect } from 'react';
import { LS_Get, LS_Set } from '../utils/local-storage';
import { trackError, ErrorSeverity } from '@/utils/error-tracking';
import {
  pageSuspendedSince,
  probeOwnOrigin,
  transportFailureContext,
} from '@/utils/fetch-transport-failure';
import { errorMessageOr } from '../utils/error-message';
import { ZodError } from 'zod';
import {
  CreateListingRequestSchema,
  CreateListingResponseSchema,
  type CreateListingRequest,
  type CreateListingResponse
} from '@/schemas/api/listings/create';
import {
  EditListingRequestSchema,
  EditListingResponseSchema,
  type EditListingRequest,
  type EditListingResponse
} from '@/schemas/api/listings/edit';
import {
  MarkSoldResponseSchema,
  type MarkSoldResponse
} from '@/schemas/api/listings/mark-sold';
import {
  BulkListingStatusResponseSchema,
  type BulkListingStatusAction,
} from '@/schemas/api/seller/listings';
import {
  ShippingEstimateResponseSchema,
  ShippingEstimateErrorResponseSchema,
  type ShippingEstimateResponse,
  type ShippingEstimateErrorResponse
} from '@/schemas/api/shipping/estimate';

/**
 * Why a single-listing status change can fail, in the seller's terms. The API
 * answers with counts (changed / skipped / notFound / failed) because it takes a
 * selection; a caller acting on ONE listing needs to say which of those happened
 * to it, so the counts are collapsed here rather than in each caller.
 */
export type ListingStatusResult = {
  ok: boolean;
  /** Only meaningful when `ok` is false. */
  reason?: 'sale-in-progress' | 'not-yours' | 'failed';
};

export interface ListingFields {
  id?: string | number;
  status?: number;
  title: string;
  text: string;
  price: string;
  brand: string;
  color: string;
  vin: string;
  mileage: string;
  category: string;
  categoryId: string;
  categoryGroupId: string;
  itemCondition: string;
  condition: string;
  shipsFrom: string;
  height: string;
  shippable: boolean;
  width: string;
  depth: string;
  weight: string;
  /**
   * Shippo USPS parcel template token; '' when the seller chose their own box;
   * absent (or null) when nothing has been chosen yet, which is when the
   * account's default packaging applies.
   */
  parcelTemplate?: string | null;
  latitude: string;
  longitude: string;
  locationText: string;
  floorPrice: string;
  recommendedPrice: string;
  /** The sell page's AI price suggestion, sent with the listing (utils/price-suggestion-signal.ts). */
  priceSuggestion?: CreateListingRequest['priceSuggestion'];
  aiPriceAnalysis?: Record<string, unknown> | null;
  photos?: unknown[];
  photosChanged?: boolean;
}

export interface ListingContextType {
  // Post Listing Data
  postImageQue: string[];
  postItemFinished: boolean;
  editListingData: ListingFields;
  postListingData: ListingFields;
  postData: ListingFields;
  coverPosition?: number;
  savePost?: () => void;
  setCoverPos?: (position: number) => void;
  postViewState?: string;
  setPostViewState?: (state: string) => void;

  // Featured listings
  featuredAisles: unknown[];

  // Post Actions
  setImageQue: (images: string[]) => void;
  setPostFields: (data: ListingFields | ((prev: ListingFields) => ListingFields)) => void;
  setPostItemFinished: (finished: boolean) => void;
  setListing: (data: ListingFields) => void;
  setLatLngOnListing: (lat: number, lng: number, edit: boolean, locationText: string) => void;

  // API Methods
  createListing: (params: CreateListingRequest) => Promise<CreateListingResponse>;
  editListing: (listingId: number | string, params: EditListingRequest) => Promise<EditListingResponse>;
  markListingSold: (listingId: number | string) => Promise<MarkSoldResponse>;
  setListingSaleStatus: (listingId: number | string, action: BulkListingStatusAction) => Promise<ListingStatusResult>;
  getShippingEstimate: (listingId: number | string, addressId?: number | string) => Promise<ShippingEstimateResponse | ShippingEstimateErrorResponse>;
}

const ListingContext = createContext<ListingContextType | undefined>(undefined);

export const useListing = () => {
  const context = useContext(ListingContext);
  if (!context) {
    throw new Error('useListing must be used within a ListingProvider');
  }
  return context;
};

interface ListingProviderProps {
  children: ReactNode;
  featuredAisles?: unknown[];
}

const defaultFields: ListingFields = {
  title: '',
  text: '',
  price: '5',
  brand: '',
  color: '',
  vin: '',
  mileage: '',
  category: '',
  categoryId: '',
  categoryGroupId: '',
  itemCondition: '',
  condition: '',
  shipsFrom: '',
  height: '',
  shippable: true,
  width: '',
  depth: '',
  weight: '',
  // Absent until the seller chooses packaging: '' is their own box, a token is a
  // template, and undefined lets the account's default packaging apply.
  latitude: '',
  longitude: '',
  locationText: '',
  floorPrice: '',
  recommendedPrice: '',
  aiPriceAnalysis: null,
  photos: [],
};

/**
 * What a seller can read from a failed request. A Zod error's `message` is its
 * whole issue list as JSON, which reached the toast verbatim ("Error Saving:
 * [ { "origin": "string", "code": "too_small" ... ]"), so it gives the first
 * issue's own message instead.
 */
function errorMessageFor(error: unknown, fallback: string): string {
  // A thrown ZodError always carries at least one issue.
  if (error instanceof ZodError) return error.issues[0].message;
  return errorMessageOr(error, fallback);
}

export const ListingProvider: React.FC<ListingProviderProps> = ({
  children,
  featuredAisles = [],
}) => {
  // Post Listing Data
  const [postImageQue, setPostImageQueState] = useState<string[]>([]);
  const [postItemFinished, setPostItemFinishedState] = useState<boolean>(false);
  const [editListingData, setEditListingData] = useState<ListingFields>({
    ...defaultFields,
    photos: [],
  });
  const [postListingData, _setPostListingData] = useState<ListingFields>({
    ...defaultFields,
    photos: [],
  });
  const [postData, setPostDataState] = useState<ListingFields>({ ...defaultFields });

  // Load saved data from localStorage after mount
  useEffect(() => {
    const savedPostData = LS_Get<Partial<ListingFields>>('postData');
    if (savedPostData) {
      // Merge with defaults to ensure all fields have valid values
      // This handles cases where localStorage has undefined/null values
      // eslint-disable-next-line react-hooks/set-state-in-effect -- hydrates saved draft from localStorage (external system unavailable during SSR) after mount to avoid hydration mismatch
      setPostDataState({
        ...defaultFields,
        ...savedPostData,
        // Ensure price has a valid default if undefined or empty
        price: savedPostData.price || defaultFields.price,
      });
    }
  }, []);

  const setImageQue = useCallback((images: string[]) => {
    setTimeout(() => {
      const slider = document.querySelector('.photos-taken') as HTMLElement;
      const sliderInner = document.querySelector('.photos-taken-slider') as HTMLElement;
      if (slider && sliderInner) {
        slider.scrollLeft = parseInt(sliderInner.style?.width || '0');
      }
    }, 100);

    setPostImageQueState(images);
  }, []);

  const setPostFields = useCallback((data: ListingFields | ((prev: ListingFields) => ListingFields)) => {
    // Support both direct value and functional update
    if (typeof data === 'function') {
      setPostDataState(prevData => {
        const newData = data(prevData);
        LS_Set('postData', newData);
        return newData;
      });
    } else {
      setPostDataState(data);
      LS_Set('postData', data);
    }
  }, []);

  const setPostItemFinished = useCallback((finished: boolean) => {
    setPostItemFinishedState(finished);
  }, []);

  const setListing = useCallback((data: ListingFields) => {
    setEditListingData(data);
  }, []);

  const setLatLngOnListing = useCallback((lat: number, lng: number, edit: boolean, locationText: string) => {
    if (edit) {
      setEditListingData(prev => ({
        ...prev,
        latitude: String(lat),
        longitude: String(lng),
        locationText,
      }));
    } else {
      setPostDataState(prev => ({
        ...prev,
        latitude: String(lat),
        longitude: String(lng),
        locationText,
      }));
    }
  }, []);

  /**
   * Create a new listing with full type safety and Zod validation
   * Replaces createListingNextAPI from network.ts
   */
  const createListing = useCallback(async (params: CreateListingRequest): Promise<CreateListingResponse> => {
    try {
      // Validate request with Zod schema
      const validatedRequest = CreateListingRequestSchema.parse(params);

      const response = await fetch('/api/listings/create', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(validatedRequest),
      });

      const data = await response.json();

      // Validate response with Zod schema
      return CreateListingResponseSchema.parse(data);
    } catch (error) {
      trackError(error, { action: 'createListing', component: 'ListingProvider' } as unknown as Parameters<typeof trackError>[1]);
      return {
        success: false,
        error: errorMessageFor(error, 'Failed to create listing')
      };
    }
  }, []);

  /**
   * Edit an existing listing with full type safety and Zod validation
   * Replaces editListingNextAPI from network.ts
   */
  const editListing = useCallback(async (listingId: number | string, params: EditListingRequest): Promise<EditListingResponse> => {
    const startedAt = Date.now();
    let requestBytes: number | null = null;
    try {
      // Validate request with Zod schema
      const validatedRequest = EditListingRequestSchema.parse(params);
      const body = JSON.stringify(validatedRequest);
      requestBytes = body.length;

      const response = await fetch(`/api/listings/${listingId}/edit`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body,
      });

      const data = await response.json();

      // Validate response with Zod schema
      return EditListingResponseSchema.parse(data);
    } catch (error) {
      // Prod 2026-10-31: one seller's saves on three new listings filed a bare
      // "Failed to fetch" with nothing else -- no listing, no timing, no page
      // state -- so the row could not say whether they left mid-save, lost the
      // network, sent a body too big to carry, or our route went down. Not
      // retried: the edit hands back photo upload slots, and a request that
      // reached us before it was cut would hand them out twice.
      const transport = transportFailureContext(error);
      const liveTransportFailure = transport.transportFailure && !transport.unloading && transport.online !== false;
      const numericId = Number(listingId);
      trackError(error, ErrorSeverity.ERROR, {
        action: 'editListing',
        component: 'ListingProvider',
        ...(Number.isFinite(numericId) ? { listing_id: numericId } : {}),
        extra: {
          ...transport,
          elapsedMs: Date.now() - startedAt,
          suspended: pageSuspendedSince(startedAt),
          requestBytes,
          photosChanged: params.photosChanged ?? null,
          // Only asked when the page was live: says whether our origin answered
          // at all, or only this route was cut.
          originReachable: liveTransportFailure ? await probeOwnOrigin() : null,
        },
      });
      return {
        success: false,
        error: errorMessageFor(error, 'Failed to edit listing')
      };
    }
  }, []);

  /**
   * Mark listing as sold with full type safety and Zod validation
   * Replaces markSoldNextAPI from network.ts
   */
  const markListingSold = useCallback(async (listingId: number | string): Promise<MarkSoldResponse> => {
    try {
      const response = await fetch(`/api/listings/${listingId}/mark-sold`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();

      // Validate response with Zod schema
      return MarkSoldResponseSchema.parse(data);
    } catch (error) {
      trackError(error, { action: 'markListingSold', component: 'ListingProvider' } as unknown as Parameters<typeof trackError>[1]);
      return {
        success: false,
        error: errorMessageFor(error, 'Failed to mark listing as sold')
      };
    }
  }, []);

  /**
   * Take one of the seller's own listings off sale, or put it back on.
   *
   * Goes through the seller-scoped bulk endpoint with a single id rather than
   * /api/listings/[id]/relist (relist only) plus /api/listings/[id]/delete
   * (delist only, and it writes the status without the matching index write).
   * bulk-status is the one path that does BOTH directions, filters every id
   * through `userId = session user`, and rolls the status back when the
   * Elasticsearch write fails -- so a listing can never end up archived in the
   * database but still publicly for sale, or live but invisible.
   */
  const setListingSaleStatus = useCallback(async (
    listingId: number | string,
    action: BulkListingStatusAction
  ): Promise<ListingStatusResult> => {
    try {
      const response = await fetch('/api/seller/listings/bulk-status', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action, listingIds: [Number(listingId)] }),
      });

      const parsed = BulkListingStatusResponseSchema.safeParse(await response.json());
      if (!parsed.success || !parsed.data.success) {
        return { ok: false, reason: 'failed' };
      }
      const { changed, skipped, notFound } = parsed.data;
      if (changed > 0) return { ok: true };
      if (notFound > 0) return { ok: false, reason: 'not-yours' };
      if (skipped > 0) return { ok: false, reason: 'sale-in-progress' };
      return { ok: false, reason: 'failed' };
    } catch (error) {
      trackError(error, { action: 'setListingSaleStatus', component: 'ListingProvider' } as unknown as Parameters<typeof trackError>[1]);
      return { ok: false, reason: 'failed' };
    }
  }, []);

  /**
   * Get shipping estimate for a listing with full type safety and Zod validation
   * Replaces getShippingEstimateNextAPI from network.ts
   */
  const getShippingEstimate = useCallback(async (
    listingId: number | string,
    addressId?: number | string
  ): Promise<ShippingEstimateResponse | ShippingEstimateErrorResponse> => {
    try {
      const url = addressId
        ? `/api/shipping/estimate?listingId=${listingId}&addressId=${addressId}`
        : `/api/shipping/estimate?listingId=${listingId}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();

      if (!response.ok) {
        return ShippingEstimateErrorResponseSchema.parse(data);
      }

      // Validate success response with Zod schema
      return ShippingEstimateResponseSchema.parse(data);
    } catch (error) {
      trackError(error, { action: 'getShippingEstimate', component: 'ListingProvider' } as unknown as Parameters<typeof trackError>[1]);
      return {
        success: false as const,
        error: errorMessageFor(error, 'Failed to fetch shipping estimate')
      };
    }
  }, []);

  const value: ListingContextType = {
    postImageQue,
    postItemFinished,
    editListingData,
    postListingData,
    postData,
    featuredAisles,
    setImageQue,
    setPostFields,
    setPostItemFinished,
    setListing,
    setLatLngOnListing,
    createListing,
    editListing,
    markListingSold,
    setListingSaleStatus,
    getShippingEstimate,
  };

  return <ListingContext.Provider value={value}>{children}</ListingContext.Provider>;
};

export default ListingProvider;
